import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, X, Copy, Check, Users, Radio, AlertTriangle } from 'lucide-react';
import { useUser } from '@/contexts/UserContext';
import Reset from '@/components/Reset';
import {
  CoachPanel, GuidePanel, ConflictPanel, ConflictCoachPanel,
  RUNGS, OL, NudgeDots,
} from '@/components/OperationListenParts';
import VoiceCaptureControl from '@/components/VoiceCaptureControl';
import {
  WaitingForHumansCard, ConflictCheckInCard, ConflictCoachCheckInCard,
} from '@/components/OperationListenRoundEnd';
import OperationListenDebrief from '@/components/OperationListenDebrief';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 2000;

const authHeader = () => {
  const t = localStorage.getItem('gntl_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

async function api(path, opts = {}) {
  const res = await fetch(`${API}/api/olg${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...authHeader(), ...(opts.headers || {}) },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || `Request failed (${res.status})`);
  return data;
}

// ------------------------ LOBBY ------------------------
const Lobby = ({ onEnterRoom, onEnterSession, onFacilitate }) => {
  const [tab, setTab] = useState('session');
  const [code, setCode] = useState('');
  const [sessionCode, setSessionCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Create-room config
  const [playerCount, setPlayerCount] = useState(4);           // 2 | 3 | 4
  const [assignmentMode, setAssignmentMode] = useState('choose'); // 'choose' | 'random'

  const handleCreate = async () => {
    setBusy(true); setError(null);
    try {
      const room = await api('/rooms', {
        method: 'POST',
        body: JSON.stringify({ playerCount, assignmentMode }),
      });
      onEnterRoom(room.code);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const handleJoin = async () => {
    if (code.length !== 4) return;
    setBusy(true); setError(null);
    try {
      await api(`/rooms/${code}`);
      onEnterRoom(code);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const handleJoinSession = async () => {
    if (sessionCode.length !== 4) return;
    setBusy(true); setError(null);
    try {
      await api(`/sessions/${sessionCode}/join`, { method: 'POST' });
      onEnterSession(sessionCode);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div data-ol-dark className="min-h-screen bg-[#1A1814] text-[#F5F2EB] flex flex-col items-center justify-center p-6 pt-safe pb-safe">
      <div className="w-full max-w-sm">
        <div className="text-[11px] font-medium tracking-[0.25em] uppercase text-[#C45A3B] mb-3 text-center">Operation Listen</div>
        <h1 className="font-serif text-4xl sm:text-5xl leading-tight text-center mb-8">
          Listen under<br /><em className="italic text-[#C45A3B]">pressure.</em>
        </h1>

        <div className="grid grid-cols-3 gap-1.5 mb-5 bg-[#252018] rounded-full p-1 border border-[#F5F2EB]/10">
          {[['session', 'Join session'], ['join', 'Join room'], ['create', 'Create room']].map(([id, label]) => (
            <button
              key={id}
              onClick={() => { setTab(id); setError(null); }}
              className={`py-2 rounded-full text-[12.5px] font-medium transition-colors ${tab === id ? 'bg-[#C45A3B] text-white' : 'text-[#F5F2EB]/60 hover:text-[#F5F2EB]'}`}
              data-testid={`olg-tab-${id}`}
            >{label}</button>
          ))}
        </div>

        {tab === 'join' && (
          <div className="space-y-4">
            <input
              inputMode="numeric" pattern="[0-9]*" maxLength={4}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="4-digit room code"
              className="w-full bg-[#252018] border border-[#F5F2EB]/10 rounded-xl text-center text-2xl font-mono tracking-[0.4em] py-4 text-[#F5F2EB] focus:outline-none focus:border-[#C45A3B]"
              data-testid="olg-code-input"
            />
            <button
              onClick={handleJoin}
              disabled={busy || code.length !== 4}
              className="w-full py-3.5 rounded-full bg-[#F5F2EB] text-[#1A1814] font-medium disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="olg-join-btn"
            >{busy ? 'Joining…' : 'Join room'}</button>
          </div>
        )}

        {tab === 'create' && (
          <div className="space-y-4">
            <p className="text-[12.5px] text-[#F5F2EB]/60 leading-relaxed text-center px-2">
              Choose how many players and how roles get handed out, then share the room code.
            </p>

            {/* Player count */}
            <div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-[#F5F2EB]/50 mb-2">Players</div>
              <div className="grid grid-cols-3 gap-1.5 bg-[#252018] rounded-full p-1 border border-[#F5F2EB]/10" data-testid="olg-create-count">
                {[2, 3, 4].map((n) => (
                  <button
                    key={n}
                    onClick={() => setPlayerCount(n)}
                    className={`py-2 rounded-full text-[12.5px] font-medium transition-colors ${playerCount === n ? 'bg-[#C45A3B] text-white' : 'text-[#F5F2EB]/60 hover:text-[#F5F2EB]'}`}
                    data-testid={`olg-create-count-${n}`}
                  >{n}-Player</button>
                ))}
              </div>
              <p className="text-[11px] text-[#F5F2EB]/40 mt-1.5 leading-snug">
                {playerCount === 4
                  ? 'Coach · Guide · Conflict · Conflict Coach'
                  : playerCount === 3
                  ? 'Coach · Guide · Conflict'
                  : 'Any two of Coach · Guide · Conflict'}
              </p>
            </div>

            {/* Assignment mode */}
            <div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-[#F5F2EB]/50 mb-2">Role assignment</div>
              <div className="grid grid-cols-2 gap-1.5 bg-[#252018] rounded-full p-1 border border-[#F5F2EB]/10" data-testid="olg-create-mode">
                {[
                  { k: 'choose', label: 'Choose' },
                  { k: 'random', label: 'Random' },
                ].map(({ k, label }) => (
                  <button
                    key={k}
                    onClick={() => setAssignmentMode(k)}
                    className={`py-2 rounded-full text-[12.5px] font-medium transition-colors ${assignmentMode === k ? 'bg-[#C45A3B] text-white' : 'text-[#F5F2EB]/60 hover:text-[#F5F2EB]'}`}
                    data-testid={`olg-create-mode-${k}`}
                  >{label}</button>
                ))}
              </div>
              <p className="text-[11px] text-[#F5F2EB]/40 mt-1.5 leading-snug">
                {assignmentMode === 'choose'
                  ? 'First come, first served — each player picks their own role.'
                  : 'Server deals a random role to each player on join.'}
              </p>
            </div>

            <button
              onClick={handleCreate}
              disabled={busy}
              className="w-full py-3.5 rounded-full bg-[#C45A3B] text-white font-medium disabled:opacity-50"
              data-testid="olg-create-btn"
            >{busy ? 'Creating…' : `Create ${playerCount}-player room`}</button>
          </div>
        )}

        {tab === 'session' && (
          <div className="space-y-3">
            <p className="text-[12.5px] text-[#F5F2EB]/60 leading-relaxed text-center px-2">
              Enter the 4-digit code your facilitator shared. You&apos;ll wait in the lobby until they split the room into groups.
            </p>
            <input
              inputMode="numeric" pattern="[0-9]*" maxLength={4}
              value={sessionCode}
              onChange={(e) => setSessionCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="4-digit session code"
              className="w-full bg-[#252018] border border-[#F5F2EB]/10 rounded-xl text-center text-2xl font-mono tracking-[0.4em] py-4 text-[#F5F2EB] focus:outline-none focus:border-[#C45A3B]"
              data-testid="olg-session-code-input"
            />
            <button
              onClick={handleJoinSession}
              disabled={busy || sessionCode.length !== 4}
              className="w-full py-3.5 rounded-full bg-[#C45A3B] text-white font-medium disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="olg-session-join-btn"
            >{busy ? 'Joining…' : 'Join session'}</button>
            <button
              onClick={onFacilitate}
              className="w-full py-3 rounded-full bg-transparent border border-[#F5F2EB]/20 text-[#F5F2EB]/80 text-sm hover:border-[#C45A3B] hover:text-[#C45A3B] transition-colors"
              data-testid="olg-facilitate-btn"
            >I&apos;m the facilitator →</button>
          </div>
        )}

        {error && <p className="text-red-400 text-sm text-center mt-4" data-testid="olg-error">{error}</p>}
      </div>
    </div>
  );
};

// ------------------------ ROLE SELECT ------------------------
const ROLE_META = {
  coach:   { title: 'Coach',          desc: 'Tap rungs you heard, send up to 2 nudges to the Guide, mark Voice + Time.' },
  guide:   { title: 'Guide',          desc: 'See incoming rungs, confirm Voice + Time, deliver the paraphrase at 7/7.' },
  playerB: { title: 'Conflict',       desc: 'Draw a rung, pick a scenario, choose your stance (Own or Blame).' },
  conflict:{ title: 'Conflict Coach', desc: 'Own the Threat Meter. Escalate / de-escalate to keep it honest.' },
};

const RoleSelect = ({ room, user, onDone, onError }) => {
  const [busy, setBusy] = useState(null);
  const [dealing, setDealing] = useState(false);
  const takenBy = {};
  Object.entries(room.players).forEach(([uid, p]) => {
    if (p.role) takenBy[p.role] = { uid, name: p.name };
  });
  const myRole = room.players[user.id]?.role || null;

  const playerCount = room.state?.playerCount || (room.state?.roleMode === '2p' ? 2 : 4);
  const assignmentMode = room.state?.assignmentMode || 'choose';
  const pool = (playerCount === 4)
    ? ['coach', 'guide', 'playerB', 'conflict']
    : ['coach', 'guide', 'playerB'];

  const pick = async (role) => {
    setBusy(role);
    try {
      await api(`/rooms/${room.code}/join`, { method: 'POST', body: JSON.stringify({ role }) });
      onDone();
    } catch (e) { onError(e.message); }
    finally { setBusy(null); }
  };

  // Random mode: auto-join without a role, let the server deal one.
  const dealRandom = React.useCallback(async () => {
    if (dealing || myRole) return;
    setDealing(true);
    try {
      await api(`/rooms/${room.code}/join`, { method: 'POST', body: JSON.stringify({}) });
      onDone();
    } catch (e) { onError(e.message); }
    finally { setDealing(false); }
  }, [dealing, myRole, room.code, onDone, onError]);

  useEffect(() => {
    if (assignmentMode === 'random' && !myRole) {
      dealRandom();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentMode, myRole]);

  if (assignmentMode === 'random') {
    return (
      <div className="space-y-3" data-testid="olg-random-dealing">
        <div className="text-[11px] tracking-[0.25em] uppercase text-[#F5F2EB]/50">Random assignment</div>
        <div className="p-5 rounded-2xl border border-[#F5F2EB]/[0.08] bg-[#252018] text-center">
          <div className="font-serif text-xl mb-2">
            {myRole ? `You are the ${ROLE_META[myRole]?.title || myRole}.` : 'Dealing roles…'}
          </div>
          <div className="text-[13px] text-[#F5F2EB]/60 leading-snug">
            {myRole
              ? ROLE_META[myRole]?.desc
              : 'The server is handing you a random free seat.'}
          </div>
        </div>
      </div>
    );
  }

  const roles = pool.map((id) => ({ id, ...ROLE_META[id] }));
  return (
    <div className="space-y-3">
      <div className="text-[11px] tracking-[0.25em] uppercase text-[#F5F2EB]/50">
        Pick your role · First come, first serve
      </div>
      {roles.map((r) => {
        const t = takenBy[r.id];
        const mine = myRole === r.id;
        const taken = t && t.uid !== user.id;
        return (
          <button
            key={r.id}
            onClick={() => pick(r.id)}
            disabled={busy || taken}
            className={`w-full text-left p-4 rounded-2xl border transition-all ${mine ? 'border-[#C45A3B] bg-[#2C2620]' : taken ? 'border-[#F5F2EB]/[0.05] bg-[#1F1B14] opacity-40 cursor-not-allowed' : 'border-[#F5F2EB]/[0.08] bg-[#252018] hover:border-[#C45A3B]/50'}`}
            data-testid={`olg-role-${r.id}`}
          >
            <div className="flex items-center justify-between mb-1">
              <div className="font-serif text-xl">{r.title}</div>
              {mine && <span className="text-[10px] uppercase tracking-[0.18em] text-[#C45A3B]">You</span>}
              {taken && <span className="text-[10px] uppercase tracking-[0.18em] text-[#F5F2EB]/65">{t.name}</span>}
            </div>
            <div className="text-[13px] text-[#F5F2EB]/60 leading-snug">{r.desc}</div>
          </button>
        );
      })}
    </div>
  );
};

// ------------------------ ROLE PANELS ------------------------
// The four role views are composed from OperationListenParts:
//   coach   → <CoachPanel />
//   guide   → <GuidePanel />
//   playerB → <ConflictPanel />          (renamed in UI, key preserved for backend)
//   conflict → <ConflictCoachPanel />    (renamed in UI, key preserved for backend)

const ModeToggle = ({ mode, onChange }) => (
  <div className="flex gap-2 bg-[#28281F] rounded-full p-1 border border-[#3A3A32]" data-testid="olg-mode-wrap">
    {['beginner', 'advanced'].map((m) => (
      <button
        key={m}
        onClick={() => onChange(m)}
        className={`flex-1 py-2 rounded-full text-[12px] font-medium capitalize transition-colors ${mode === m ? 'bg-[#F5F2EB] text-[#141410]' : 'text-[#E8E4DC]/60'}`}
        data-testid={`olg-mode-${m}`}
      >{m}</button>
    ))}
  </div>
);

// ------------------------ IN-ROOM ------------------------
const RoomView = ({ room, user, error, onRefresh, onExit }) => {
  const me = room.players[user.id];
  const hasRole = !!me?.role;
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [openDebrief, setOpenDebrief] = useState(null);
  const rolesInRoom = Object.values(room.players).map((p) => p.role).filter(Boolean);
  const coachPresent = rolesInRoom.includes('coach');
  const isCoach = me?.role === 'coach';
  // The player who can advance / end a round: Coach by default; otherwise the
  // Guide (in 2P/3P games that chose not to seat a Coach). Falls back to the
  // room creator so there's always someone who can close out the round.
  const canControlRound = isCoach
    || (me?.role === 'guide' && !coachPresent)
    || (!coachPresent && user.id === room.createdBy);
  const needsGate = me?.role === 'coach' || me?.role === 'guide';
  const roundComplete = !!(room?.state?.paraphraseSubmitted
    && room?.state?.respectComplete
    && room?.state?.nextStepsComplete);
  const roleModeKey = room?.state?.roleMode || '4p';
  // Gate stages: 'ask' → (optional 'reset') → 'enter' → 'in-game'
  const gateKey = me ? `ol_gate_${room.code}_${me.role}` : null;
  const [gateStage, setGateStage] = useState(() => {
    if (!needsGate) return 'in-game';
    try {
      const stored = gateKey && localStorage.getItem(gateKey);
      return stored === 'done' ? 'in-game' : 'ask';
    } catch (_) { return 'ask'; }
  });

  // If the user picks a role after the room loads, re-init the gate
  useEffect(() => {
    if (!needsGate) { setGateStage('in-game'); return; }
    try {
      const stored = gateKey && localStorage.getItem(gateKey);
      if (stored === 'done') setGateStage('in-game');
      else setGateStage((s) => (s === 'in-game' ? 'ask' : s));
    } catch (_) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.role, gateKey]);

  const markGateDone = () => {
    setGateStage('in-game');
    try { gateKey && localStorage.setItem(gateKey, 'done'); } catch (_) {}
  };

  const copyCode = () => {
    navigator.clipboard?.writeText(room.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  const write = async (patch) => {
    try { await api(`/rooms/${room.code}/state`, { method: 'POST', body: JSON.stringify(patch) }); onRefresh(); }
    catch (e) { console.error(e); }
  };

  const nextRound = async () => { setBusy(true); await api(`/rooms/${room.code}/next-round`, { method: 'POST' }).catch(() => {}); onRefresh(); setBusy(false); };
  const endGame = async () => { setBusy(true); await api(`/rooms/${room.code}/end`, { method: 'POST' }).catch(() => {}); onExit(); };

  return (
    <div data-ol-dark className="h-[100dvh] pt-safe flex flex-col overflow-hidden" style={{ background: OL.bg, color: OL.text }}>
      <div className="max-w-md w-full mx-auto p-4 pb-3 flex-1 flex flex-col min-h-0 gap-3">
        <div className="flex items-center justify-between">
          <button onClick={onExit} className="flex items-center gap-1 text-sm" style={{ color: OL.textDim }} data-testid="olg-exit">
            <ArrowLeft className="w-4 h-4" /> Exit
          </button>
          <button onClick={copyCode} className="flex items-center gap-2 px-3 py-1.5 rounded-full text-[13px]"
            style={{ background: OL.surface, border: `1px solid ${OL.border}` }} data-testid="olg-copy-code">
            <span style={{ color: OL.textMuted, fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase' }}>Room</span>
            <span className="font-mono tracking-[0.3em]">{room.code}</span>
            {copied ? <Check className="w-3.5 h-3.5" style={{ color: OL.stable }} /> : <Copy className="w-3.5 h-3.5" style={{ color: OL.textDim }} />}
          </button>
        </div>

        <div className="flex flex-wrap gap-2 text-[11px]">
          {Object.entries(room.players).map(([uid, p]) => (
            <span key={uid} className="px-2.5 py-1 rounded-full"
              style={{
                border: `1px solid ${uid === user.id ? OL.clay : OL.border2}`,
                color: uid === user.id ? OL.clay : OL.textDim,
              }}>
              {p.name} · {p.role ? (ROLE_LABELS[p.role] || p.role) : 'picking…'}
            </span>
          ))}
        </div>

        {!hasRole ? (
          <RoleSelect room={room} user={user} onDone={onRefresh} onError={() => {}} />
        ) : gateStage !== 'in-game' ? (
          <PreGameGate
            role={me.role}
            stage={gateStage}
            onSetStage={setGateStage}
            onDone={markGateDone}
          />
        ) : (
          <div className="flex-1 min-h-0 flex flex-col gap-2 overflow-hidden">
            <div style={{ fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', color: OL.textMuted }}>
              Round {room.state.roundNum || 1}
            </div>

            {/* End-of-round cards — role-specific, shown as soon as the round
                finishes. Guide/Coach see the scoring summary + the three-
                choice prompt; Conflict / CC see their questionnaires. */}
            {roundComplete && (me.role === 'coach'
              || (me.role === 'guide' && !coachPresent)) && (
              <WaitingForHumansCard
                roomCode={room.code}
                roleMode={roleModeKey}
                canControl={canControlRound}
                onOpenDebrief={(d) => setOpenDebrief(d)}
                onNextRound={nextRound}
                onEndGame={endGame}
              />
            )}
            {roundComplete && me.role === 'guide' && coachPresent && (
              <WaitingForHumansCard
                roomCode={room.code}
                roleMode={roleModeKey}
                canControl={false}
                onOpenDebrief={(d) => setOpenDebrief(d)}
              />
            )}
            {roundComplete && me.role === 'playerB' && (
              <ConflictCheckInCard
                roomCode={room.code}
                onOpenDebrief={(d) => setOpenDebrief(d)} />
            )}
            {roundComplete && me.role === 'conflict' && (
              <ConflictCoachCheckInCard
                roomCode={room.code}
                onOpenDebrief={(d) => setOpenDebrief(d)} />
            )}

            <div className="flex-1 min-h-0 overflow-y-auto pr-1" style={{ scrollbarWidth: 'thin' }}>
              {me.role === 'coach' && <CoachPanel state={room.state} write={write} testPrefix="olg" />}
              {me.role === 'guide' && (
                !coachPresent
                  ? <CoachPanel state={room.state} write={write} testPrefix="olg" panelTitle="Guide"
                      voiceCapture={<VoiceCaptureControl actor="guide" roomCode={room.code}
                        activeRound={gateStage === 'in-game' && !roundComplete} testId="olg-voice-guide"
                        roomState={room.state} writeState={write} />} />
                  : <GuidePanel state={room.state} write={write} testPrefix="olg"
                      voiceCapture={<VoiceCaptureControl actor="guide" roomCode={room.code}
                        activeRound={gateStage === 'in-game' && !roundComplete} testId="olg-voice-guide"
                        roomState={room.state} writeState={write} />} />
              )}
              {me.role === 'playerB' && <ConflictPanel state={room.state} write={write} testPrefix="olg"
                voiceCapture={<VoiceCaptureControl actor="playerB" roomCode={room.code}
                  activeRound={gateStage === 'in-game' && !roundComplete} testId="olg-voice-conflict"
                  roomState={room.state} writeState={write} />} />}
              {me.role === 'conflict' && <ConflictCoachPanel state={room.state} write={write} testPrefix="olg" />}
            </div>

            {canControlRound && !roundComplete && (
              <div className="pt-2 flex gap-2 flex-shrink-0" style={{ borderTop: `1px solid ${OL.border}` }}>
                <button onClick={nextRound} disabled={busy}
                  className="flex-1 py-2.5 rounded-full text-sm"
                  style={{ background: OL.surface, border: `1px solid ${OL.border2}`, color: OL.text }}
                  data-testid="olg-next-round">Next round</button>
                <button onClick={endGame} disabled={busy}
                  className="flex-1 py-2.5 rounded-full text-sm"
                  style={{ background: 'transparent', border: `1px solid ${OL.danger}66`, color: '#F87171' }}
                  data-testid="olg-end-game">End game</button>
              </div>
            )}
          </div>
        )}

        {error && <p className="text-red-400 text-sm" data-testid="olg-error">{error}</p>}
      </div>

      {/* Post-round analysis overlay — shown when any role taps View analysis. */}
      {openDebrief && (
        <OperationListenDebrief
          debrief={openDebrief}
          viewerRole={me?.role || 'facilitator'}
          onSubmitHumanAnswer={async (lane, answers) => {
            try {
              await api(`/debriefs/${openDebrief.id}/human-answer`, {
                method: 'POST', body: JSON.stringify({ lane, answers }),
              });
            } catch (_) {}
          }}
          onStartNewRound={canControlRound ? async () => {
            setOpenDebrief(null);
            await nextRound();
          } : undefined}
          onClose={() => setOpenDebrief(null)}
        />
      )}
    </div>
  );
};

const ROLE_LABELS = {
  coach: 'Coach',
  guide: 'Guide',
  playerB: 'Conflict',
  conflict: 'Conflict Coach',
};

// Pre-game gate — Coach + Guide only. Optional Reset run, then Enter Game.
const PreGameGate = ({ role, stage, onSetStage, onDone }) => {
  if (stage === 'reset') {
    // Full-screen physical-architecture Reset module. Exit → advance to 'enter'.
    return <Reset onExit={() => onSetStage('enter')} />;
  }
  const roleLabel = ROLE_LABELS[role] || role;
  return (
    <div
      className="rounded-2xl p-6 sm:p-8"
      style={{ background: OL.surface, border: `1px solid ${OL.border}` }}
      data-testid={`olg-pregame-${role}`}
    >
      <div style={{
        fontSize: 10, letterSpacing: '0.24em', textTransform: 'uppercase',
        color: OL.clay, marginBottom: 12,
      }}>Before you play · {roleLabel}</div>

      {stage === 'ask' && (
        <>
          <h2 style={{
            fontFamily: '"DM Serif Display", Georgia, serif',
            fontSize: 26, lineHeight: 1.15, color: OL.cream, marginBottom: 10,
          }}>Reset first?</h2>
          <p style={{ fontSize: 13, color: OL.textDim, lineHeight: 1.6, marginBottom: 20 }}>
            A quick physical reset drops your shoulders, chin, and breath into place
            before you have to listen under pressure. Two minutes.
          </p>
          <div className="flex gap-2" data-testid="olg-pregame-ask">
            <button
              onClick={() => onSetStage('reset')}
              className="flex-1 py-3 rounded-full text-sm font-semibold"
              style={{ background: OL.clay, color: '#fff', border: 0 }}
              data-testid="olg-pregame-yes"
            >Yes — reset</button>
            <button
              onClick={() => onSetStage('enter')}
              className="flex-1 py-3 rounded-full text-sm"
              style={{ background: 'transparent', border: `1px solid ${OL.border2}`, color: OL.text }}
              data-testid="olg-pregame-no"
            >No — skip</button>
          </div>
        </>
      )}

      {stage === 'enter' && (
        <>
          <h2 style={{
            fontFamily: '"DM Serif Display", Georgia, serif',
            fontSize: 26, lineHeight: 1.15, color: OL.cream, marginBottom: 10,
          }}>You're set.</h2>
          <p style={{ fontSize: 13, color: OL.textDim, lineHeight: 1.6, marginBottom: 20 }}>
            When you tap Enter Game, the other players will see your presence and
            the room becomes live. Take one breath first.
          </p>
          <button
            onClick={onDone}
            className="w-full py-3.5 rounded-full text-sm font-semibold"
            style={{ background: OL.clay, color: '#fff', border: 0 }}
            data-testid="olg-pregame-enter"
          >Enter Game →</button>
        </>
      )}
    </div>
  );
};

// ------------------------ FACILITATOR FLOW ------------------------

const FacilitatorSetup = ({ onCreated, onCancel }) => {
  const [numGroups, setNumGroups] = useState(3);
  const [mode, setMode] = useState('beginner');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const create = async () => {
    setBusy(true); setError(null);
    try {
      const sess = await api('/sessions', {
        method: 'POST',
        body: JSON.stringify({ numGroups, gameMode: mode }),
      });
      onCreated(sess.code);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div data-ol-dark className="min-h-screen bg-[#1A1814] text-[#F5F2EB] p-6 pt-safe pb-safe">
      <div className="max-w-md mx-auto">
        <button onClick={onCancel} className="text-[#F5F2EB]/60 hover:text-[#F5F2EB] flex items-center gap-1 text-sm mb-6" data-testid="olg-fac-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="text-[11px] font-medium tracking-[0.25em] uppercase text-[#C45A3B] mb-2">Facilitator</div>
        <h1 className="font-serif text-3xl mb-6">Create a session</h1>

        <div className="space-y-6">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#F5F2EB]/50 mb-2">Number of groups</div>
            <div className="flex gap-2 flex-wrap" data-testid="olg-fac-numgroups">
              {[2, 3, 4, 5, 6, 7, 8].map((n) => (
                <button
                  key={n}
                  onClick={() => setNumGroups(n)}
                  className={`w-10 h-10 rounded-full text-sm font-medium transition-colors ${numGroups === n ? 'bg-[#C45A3B] text-white' : 'bg-[#252018] border border-[#F5F2EB]/10 text-[#F5F2EB]/70'}`}
                  data-testid={`olg-fac-group-${n}`}
                >{n}</button>
              ))}
            </div>
            <div className="text-[11px] text-[#F5F2EB]/65 mt-2">Players will be split evenly across {numGroups} rooms of 3–4.</div>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#F5F2EB]/50 mb-2">Game mode</div>
            <ModeToggle mode={mode} onChange={setMode} />
          </div>

          <button
            onClick={create}
            disabled={busy}
            className="w-full py-4 rounded-full bg-[#C45A3B] text-white font-medium disabled:opacity-50"
            data-testid="olg-fac-create"
          >{busy ? 'Creating…' : 'Create session'}</button>

          {error && <p className="text-red-400 text-sm text-center" data-testid="olg-fac-error">{error}</p>}
        </div>
      </div>
    </div>
  );
};

const CustomNudge = ({ groupNum, room, busy, onSend }) => {
  const [msg, setMsg] = useState('');
  const [target, setTarget] = useState('conflict_coach');
  const hasConflict = Object.values(room?.players || {}).some((p) => p.role === 'conflict');
  const hasPlayerB = Object.values(room?.players || {}).some((p) => p.role === 'playerB');

  // Auto-shift to whichever role is available
  useEffect(() => {
    if (target === 'conflict_coach' && !hasConflict && hasPlayerB) setTarget('player_b');
    if (target === 'player_b' && !hasPlayerB && hasConflict) setTarget('conflict_coach');
  }, [hasConflict, hasPlayerB, target]);

  const canSend = !busy && msg.trim().length > 0 && msg.length <= 240 && ((target === 'conflict_coach' && hasConflict) || (target === 'player_b' && hasPlayerB));

  const send = () => {
    if (!canSend) return;
    onSend(target, msg.trim());
    setMsg('');
  };

  const targetLabel = target === 'conflict_coach' ? 'Conflict Coach' : 'Player B';

  return (
    <div className="pt-2 space-y-2" data-testid={`olg-fac-custom-${groupNum}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#F5F2EB]/50">Custom nudge</div>
        <div className="flex gap-1 bg-[#1A1814] rounded-full p-0.5 border border-[#F5F2EB]/10">
          <button
            onClick={() => setTarget('conflict_coach')}
            disabled={!hasConflict}
            className={`px-2.5 py-0.5 rounded-full text-[10px] transition-colors ${target === 'conflict_coach' ? 'bg-[#C45A3B] text-white' : 'text-[#F5F2EB]/60'} disabled:opacity-30`}
            data-testid={`olg-fac-custom-target-conflict-${groupNum}`}
          >Conflict</button>
          <button
            onClick={() => setTarget('player_b')}
            disabled={!hasPlayerB}
            className={`px-2.5 py-0.5 rounded-full text-[10px] transition-colors ${target === 'player_b' ? 'bg-[#D6A85A] text-[#1A1814]' : 'text-[#F5F2EB]/60'} disabled:opacity-30`}
            data-testid={`olg-fac-custom-target-b-${groupNum}`}
          >Player B</button>
        </div>
      </div>
      <div className="flex gap-2">
        <input
          value={msg}
          onChange={(e) => setMsg(e.target.value.slice(0, 240))}
          onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
          placeholder={`Short nudge to ${targetLabel.toLowerCase()}…`}
          className="flex-1 bg-[#1A1814] border border-[#F5F2EB]/10 rounded-full px-3 py-2 text-[12px] text-[#F5F2EB] placeholder:text-[#F5F2EB]/60 focus:outline-none focus:border-[#C45A3B]"
          data-testid={`olg-fac-custom-input-${groupNum}`}
        />
        <button
          onClick={send}
          disabled={!canSend}
          className="px-3 py-2 rounded-full bg-[#F5F2EB] text-[#1A1814] text-[11px] font-medium disabled:opacity-30"
          data-testid={`olg-fac-custom-send-${groupNum}`}
        >Send</button>
      </div>
      <div className="text-[10px] text-[#F5F2EB]/60 text-right">{msg.length}/240</div>
    </div>
  );
};

const FacilitatorDashboard = ({ session, rooms, onNudge, onSplit, onDebrief, onEnd, onExit, error }) => {
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const canSplit = session.sessionStatus === 'lobby' && (session.lobbyCount || 0) > 0;
  const canDebrief = session.sessionStatus === 'active';
  const isEnded = session.sessionStatus === 'ended';

  const copyCode = () => {
    navigator.clipboard?.writeText(session.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  const wrap = async (fn) => {
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  };

  const roomByGroup = useMemo(() => {
    const m = {};
    (rooms || []).forEach((r) => { m[r.groupNum] = r; });
    return m;
  }, [rooms]);

  const threatColor = (n) => n >= 4 ? 'bg-[#C45A3B]' : n >= 3 ? 'bg-[#D6A85A]' : n >= 1 ? 'bg-[#D6A85A]/70' : 'bg-[#F5F2EB]/[0.1]';

  return (
    <div data-ol-dark className="min-h-screen bg-[#1A1814] text-[#F5F2EB] p-5 pt-safe pb-safe">
      <div className="max-w-3xl mx-auto space-y-5">
        <div className="flex items-center justify-between">
          <button onClick={onExit} className="text-[#F5F2EB]/60 hover:text-[#F5F2EB] flex items-center gap-1 text-sm" data-testid="olg-fac-exit">
            <ArrowLeft className="w-4 h-4" /> Exit
          </button>
          <button onClick={copyCode} className="flex items-center gap-2 bg-[#252018] px-3 py-1.5 rounded-full border border-[#F5F2EB]/10 text-[13px]" data-testid="olg-fac-copy">
            <span className="text-[#F5F2EB]/50 text-[10px] uppercase tracking-[0.18em]">Session</span>
            <span className="font-mono tracking-[0.3em]">{session.code}</span>
            {copied ? <Check className="w-3.5 h-3.5 text-[#86AC93]" /> : <Copy className="w-3.5 h-3.5 text-[#F5F2EB]/65" />}
          </button>
        </div>

        <div>
          <div className="text-[11px] font-medium tracking-[0.25em] uppercase text-[#C45A3B]">Facilitator</div>
          <h1 className="font-serif text-3xl mt-1">Session dashboard</h1>
          <div className="text-[13px] text-[#F5F2EB]/50 mt-1">
            {session.numGroups} groups · {session.gameMode} mode · status: {session.sessionStatus}
          </div>
        </div>

        {session.sessionStatus === 'lobby' && (
          <div className="bg-[#252018] rounded-2xl p-5 border border-[#F5F2EB]/[0.08]" data-testid="olg-fac-lobby">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#F5F2EB]/50 mb-3">
              <Users className="w-3.5 h-3.5" /> Lobby
            </div>
            <div className="font-serif text-4xl text-[#C45A3B] mb-1" data-testid="olg-fac-lobby-count">{session.lobbyCount || 0}</div>
            <div className="text-[13px] text-[#F5F2EB]/60">
              players joined. Share the session code — then Split when you're ready.
            </div>
            <button
              onClick={() => wrap(onSplit)}
              disabled={!canSplit || busy}
              className="mt-4 w-full py-3.5 rounded-full bg-[#C45A3B] text-white font-medium disabled:opacity-40"
              data-testid="olg-fac-split"
            >{busy ? 'Splitting…' : `Split into ${session.numGroups} groups`}</button>
          </div>
        )}

        {(session.sessionStatus === 'active' || session.sessionStatus === 'debrief') && (
          <>
            <div className="space-y-3">
              {(session.groups || []).map((g) => {
                const room = roomByGroup[g.groupNum];
                const s = room?.state || {};
                const rungsHeard = (s.rungs || []).filter(Boolean).length;
                const activeNudges = (s.nudged || []).filter(Boolean).length;
                const nudgeCap = (s.gameMode || 'beginner') === 'beginner' ? 7 : 2;
                const threat = s.threat || 1;
                const roundStatus = s.phase === 'debrief' ? 'Debrief' : 'Active';
                const expanded = expandedGroup === g.groupNum;
                return (
                  <div key={g.groupNum} className="bg-[#252018] rounded-2xl border border-[#F5F2EB]/[0.08] overflow-hidden" data-testid={`olg-fac-group-${g.groupNum}`}>
                    <button
                      onClick={() => setExpandedGroup(expanded ? null : g.groupNum)}
                      className="w-full p-4 flex items-center gap-3 text-left"
                      data-testid={`olg-fac-group-toggle-${g.groupNum}`}
                    >
                      <div className="w-9 h-9 rounded-full bg-[#1A1814] border border-[#C45A3B]/40 flex items-center justify-center font-serif text-[#C45A3B]">{g.groupNum}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2">
                          <span className="text-[13px] font-mono text-[#F5F2EB]/60">room {g.roomCode}</span>
                          <span className={`text-[10px] uppercase tracking-[0.18em] ${roundStatus === 'Debrief' ? 'text-[#86AC93]' : 'text-[#F5F2EB]/50'}`}>{roundStatus}</span>
                        </div>
                        <div className="text-[13px] text-[#F5F2EB]/80 truncate mt-0.5">
                          <Radio className="inline w-3 h-3 mr-1 -mt-0.5 text-[#C45A3B]" />{rungsHeard}/7 rungs · nudges {activeNudges}/{nudgeCap}
                        </div>
                      </div>
                      <div className="flex gap-1" data-testid={`olg-fac-threat-${g.groupNum}`}>
                        {[1, 2, 3, 4].map((n) => (
                          <div key={n} className="w-1.5 h-4 rounded-full"
                            style={{ background: n <= threat ? (threat >= 4 ? '#9B2525' : threat >= 3 ? '#C45A3B' : threat >= 2 ? '#D4863A' : '#2D6B4F') : 'rgba(245,242,235,0.08)' }} />
                        ))}
                      </div>
                    </button>

                    {expanded && (
                      <div className="border-t border-[#F5F2EB]/[0.08] p-4 space-y-3" data-testid={`olg-fac-detail-${g.groupNum}`}>
                        <div className="flex flex-wrap gap-1.5">
                          {Object.entries(room?.players || {}).map(([uid, p]) => (
                            <span key={uid} className="px-2 py-0.5 rounded-full border border-[#F5F2EB]/15 text-[10px] text-[#F5F2EB]/70">{p.name} · {ROLE_LABELS[p.role] || p.role}</span>
                          ))}
                          {!Object.keys(room?.players || {}).length && <span className="text-[11px] text-[#F5F2EB]/65">waiting for players to pick roles…</span>}
                        </div>

                        <div className="grid grid-cols-7 gap-1">
                          {RUNGS.map((r, i) => {
                            const tapped = !!(s.rungs || [])[i];
                            const nudged = !!(s.nudged || [])[i];
                            return (
                              <div key={r.num}
                                className={`rounded-md px-1 py-1.5 text-center border ${nudged ? 'border-[#C45A3B] bg-[#2C2620]' : tapped ? 'border-[#C45A3B]/40 bg-[#2C2620]/50' : 'border-[#F5F2EB]/[0.08]'}`}
                                title={r.name}>
                                <div className={`text-[11px] font-mono ${nudged ? 'text-[#C45A3B]' : tapped ? 'text-[#C45A3B]/70' : 'text-[#F5F2EB]/65'}`}>{r.num}</div>
                              </div>
                            );
                          })}
                        </div>

                        <div className="flex gap-2 pt-1">
                          <button
                            onClick={() => wrap(() => onNudge(g.groupNum, 'conflict_coach'))}
                            disabled={busy || !Object.values(room?.players || {}).some((p) => p.role === 'conflict')}
                            className="flex-1 py-2 rounded-full bg-[#252018] border border-[#C45A3B]/40 text-[#C45A3B] text-[12px] disabled:opacity-30"
                            data-testid={`olg-fac-nudge-conflict-${g.groupNum}`}
                          >Nudge Conflict Coach</button>
                          <button
                            onClick={() => wrap(() => onNudge(g.groupNum, 'player_b'))}
                            disabled={busy || !Object.values(room?.players || {}).some((p) => p.role === 'playerB')}
                            className="flex-1 py-2 rounded-full bg-[#252018] border border-[#D6A85A]/40 text-[#D6A85A] text-[12px] disabled:opacity-30"
                            data-testid={`olg-fac-nudge-b-${g.groupNum}`}
                          >Nudge Conflict player</button>
                        </div>

                        <CustomNudge
                          groupNum={g.groupNum}
                          room={room}
                          busy={busy}
                          onSend={(target, message) => wrap(() => onNudge(g.groupNum, target, message))}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="pt-4 border-t border-[#F5F2EB]/[0.08] flex gap-2">
              <button
                onClick={() => wrap(onDebrief)}
                disabled={busy || !canDebrief}
                className="flex-1 py-3 rounded-full bg-[#252018] border border-[#86AC93]/50 text-[#86AC93] text-sm disabled:opacity-40"
                data-testid="olg-fac-debrief"
              >Call whole-room debrief</button>
              <button
                onClick={() => wrap(onEnd)}
                disabled={busy || isEnded}
                className="flex-1 py-3 rounded-full bg-transparent border border-red-500/40 text-red-400 text-sm disabled:opacity-40"
                data-testid="olg-fac-end"
              >End session</button>
            </div>
          </>
        )}

        {isEnded && (
          <div className="bg-[#252018] rounded-2xl p-5 border border-[#F5F2EB]/[0.08] text-center">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#F5F2EB]/50 mb-2">Session ended</div>
            <div className="text-[13px] text-[#F5F2EB]/60 italic">
              "Was there a moment you knew what to do — but didn't do it? What was in the way?"
            </div>
          </div>
        )}

        {session.nudgeLog && session.nudgeLog.length > 0 && (
          <div className="pt-3 text-[11px] text-[#F5F2EB]/65" data-testid="olg-fac-nudge-log">
            Nudges sent: {session.nudgeLog.length}
          </div>
        )}

        {error && <p className="text-red-400 text-sm" data-testid="olg-fac-err">{error}</p>}
      </div>
    </div>
  );
};

const FacilitatorFlow = ({ initialCode, onExit }) => {
  const [code, setCode] = useState(initialCode || null);
  const [session, setSession] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [error, setError] = useState(null);
  const timer = useRef(null);

  const refresh = useCallback(async () => {
    if (!code) return;
    try {
      const [s, r] = await Promise.all([
        api(`/sessions/${code}`),
        api(`/sessions/${code}/rooms`),
      ]);
      setSession(s);
      setRooms(r.rooms || []);
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  }, [code]);

  useEffect(() => {
    if (!code) return undefined;
    refresh();
    timer.current = setInterval(refresh, POLL_MS);
    return () => clearInterval(timer.current);
  }, [code, refresh]);

  const doSplit = async () => {
    await api(`/sessions/${code}/split`, { method: 'POST' });
    refresh();
  };
  const doNudge = async (groupNum, target, message) => {
    const body = { groupNum, target };
    if (message) body.message = message;
    await api(`/sessions/${code}/nudge`, { method: 'POST', body: JSON.stringify(body) });
    refresh();
  };
  const doDebrief = async () => {
    await api(`/sessions/${code}/debrief`, { method: 'POST' });
    refresh();
  };
  const doEnd = async () => {
    await api(`/sessions/${code}/end`, { method: 'POST' });
    onExit();
  };

  if (!code) return <FacilitatorSetup onCreated={setCode} onCancel={onExit} />;
  if (!session) return <div data-ol-dark className="min-h-screen bg-[#1A1814] text-[#F5F2EB] flex items-center justify-center">Loading session {code}…</div>;
  return (
    <FacilitatorDashboard
      session={session}
      rooms={rooms}
      onSplit={doSplit}
      onNudge={doNudge}
      onDebrief={doDebrief}
      onEnd={doEnd}
      onExit={onExit}
      error={error}
    />
  );
};

// ------------------------ SESSION PLAYER (Lobby → Assigned Room) ------------------------
const SessionPlayer = ({ sessionCode, onExit, onEnterRoom }) => {
  const [session, setSession] = useState(null);
  const [error, setError] = useState(null);
  const timer = useRef(null);

  const fetchSess = useCallback(async () => {
    try {
      const s = await api(`/sessions/${sessionCode}`);
      setSession(s);
      setError(null);
      if (s.myGroup?.roomCode) {
        // Player has been assigned — enter their room.
        clearInterval(timer.current);
        onEnterRoom(s.myGroup.roomCode);
      }
    } catch (e) {
      setError(e.message);
    }
  }, [sessionCode, onEnterRoom]);

  useEffect(() => {
    fetchSess();
    timer.current = setInterval(fetchSess, POLL_MS);
    return () => clearInterval(timer.current);
  }, [fetchSess]);

  return (
    <div data-ol-dark className="min-h-screen bg-[#1A1814] text-[#F5F2EB] flex flex-col items-center justify-center p-6 pt-safe pb-safe">
      <div className="w-full max-w-sm text-center">
        <button onClick={onExit} className="text-[#F5F2EB]/50 hover:text-[#F5F2EB] flex items-center gap-1 text-sm mb-8 mx-auto" data-testid="olg-sess-exit">
          <ArrowLeft className="w-4 h-4" /> Leave lobby
        </button>
        <div className="text-[11px] uppercase tracking-[0.25em] text-[#C45A3B] mb-2">Session</div>
        <div className="font-mono text-3xl tracking-[0.3em] mb-6">{sessionCode}</div>

        <div className="bg-[#252018] rounded-2xl p-6 border border-[#F5F2EB]/[0.08]">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#F5F2EB]/50 mb-2">Waiting in lobby</div>
          <div className="text-[13px] text-[#F5F2EB]/70 leading-relaxed">
            You're in. When the facilitator splits the room, you'll be routed to your group automatically.
          </div>
          <div className="text-[11px] text-[#F5F2EB]/65 mt-4" data-testid="olg-sess-lobby-count">
            {session ? `${session.lobbyCount || 0} in lobby` : 'Connecting…'}
          </div>
        </div>

        {error && <p className="text-red-400 text-sm mt-4" data-testid="olg-sess-err">{error}</p>}
      </div>
    </div>
  );
};

// ------------------------ NUDGE OVERLAY (private, fades in 8s) ------------------------
const NudgeOverlay = ({ nudge, onDismiss }) => {
  useEffect(() => {
    if (!nudge) return undefined;
    const t = setTimeout(onDismiss, 8000);
    return () => clearTimeout(t);
  }, [nudge, onDismiss]);

  if (!nudge) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/60 backdrop-blur-sm animate-in fade-in" data-testid="olg-nudge-overlay">
      <div className="max-w-sm w-full bg-[#252018] border border-[#C45A3B] rounded-2xl p-6 shadow-2xl">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#C45A3B] mb-3">
          <AlertTriangle className="w-3.5 h-3.5" /> Referee nudge
          {nudge.custom && <span className="ml-auto text-[9px] tracking-[0.22em] text-[#D6A85A]/80">custom</span>}
        </div>
        <div className="text-[14px] text-[#F5F2EB] leading-relaxed mb-5" data-testid="olg-nudge-text">
          {nudge.message}
        </div>
        <button
          onClick={onDismiss}
          className="w-full py-2.5 rounded-full bg-[#C45A3B] text-white text-sm font-medium"
          data-testid="olg-nudge-dismiss"
        >Got it</button>
      </div>
    </div>
  );
};

// ------------------------ TOP-LEVEL ------------------------
const OperationListen = ({ onExit, initialMode }) => {
  const { user } = useUser();
  const [mode, setMode] = useState(initialMode || 'lobby'); // lobby | room | session | facilitator
  const [code, setCode] = useState(null);
  const [sessionCode, setSessionCode] = useState(null);
  const [room, setRoom] = useState(null);
  const [error, setError] = useState(null);
  const [nudge, setNudge] = useState(null);
  const timer = useRef(null);

  const fetchRoom = useCallback(async () => {
    if (!code) return;
    try {
      const r = await api(`/rooms/${code}`);
      setRoom(r);
      setError(null);
    } catch (e) {
      setError(e.message);
      if (String(e.message).toLowerCase().includes('not found')) {
        setCode(null); setRoom(null); setMode('lobby');
      }
    }
    // If this room belongs to a session and the player is targeted, pull pending nudge
    if (sessionCode) {
      try {
        const s = await api(`/sessions/${sessionCode}`);
        if (s.pendingNudge && !nudge) setNudge(s.pendingNudge);
      } catch (_) { /* ignore */ }
    }
  }, [code, sessionCode, nudge]);

  useEffect(() => {
    if (mode !== 'room' || !code) return undefined;
    fetchRoom();
    timer.current = setInterval(fetchRoom, POLL_MS);
    return () => clearInterval(timer.current);
  }, [mode, code, fetchRoom]);

  const dismissNudge = async () => {
    if (sessionCode) {
      try { await api(`/sessions/${sessionCode}/nudge/ack`, { method: 'POST' }); } catch (_) {}
    }
    setNudge(null);
  };

  const backToLobby = () => {
    setMode('lobby');
    setCode(null);
    setRoom(null);
    setSessionCode(null);
    setError(null);
    setNudge(null);
  };

  if (!user) {
    return (
      <div data-ol-dark className="min-h-screen bg-[#1A1814] text-[#F5F2EB] flex items-center justify-center p-6">
        <p className="text-sm text-[#F5F2EB]/60">Sign in to play Operation Listen.</p>
      </div>
    );
  }

  if (mode === 'facilitator') {
    return <FacilitatorFlow onExit={backToLobby} />;
  }

  if (mode === 'session' && sessionCode) {
    return (
      <SessionPlayer
        sessionCode={sessionCode}
        onExit={backToLobby}
        onEnterRoom={(rc) => { setCode(rc); setMode('room'); }}
      />
    );
  }

  if (mode === 'room' && code) {
    if (!room) return <div data-ol-dark className="min-h-screen bg-[#1A1814] text-[#F5F2EB] flex items-center justify-center">Loading room {code}…</div>;
    return (
      <>
        <RoomView room={room} user={user} error={error} onRefresh={fetchRoom} onExit={onExit || backToLobby} />
        <NudgeOverlay nudge={nudge} onDismiss={dismissNudge} />
      </>
    );
  }

  return (
    <Lobby
      onEnterRoom={(rc) => { setCode(rc); setMode('room'); }}
      onEnterSession={(sc) => { setSessionCode(sc); setMode('session'); }}
      onFacilitate={() => setMode('facilitator')}
    />
  );
};

export default OperationListen;
